import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Chip,
  Skeleton,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import Header from "components/Header";

const ClonePage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      //   const res = await axios.get(`${apiUrl}/pages`);
      const token = localStorage.getItem("token");

      const res = await axios.get(`${apiUrl}/pages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      //   setPages(res.data || []);
      setPages(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch pages:", err);
      setPages([]);
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (pageId, e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure? This cannot be undone.")) return;

    try {
      setDeletingId(pageId);
      const token = localStorage.getItem("token");
      await axios.delete(`${apiUrl}/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPages((prev) => prev.filter((p) => p._id !== pageId));
    } catch (err) {
      alert("Failed to delete page.");
    } finally {
      setDeletingId(null);
    }
  };
  const [openCloneModal, setOpenCloneModal] = useState(false);

  const handleOpenCloneModal = () => setOpenCloneModal(true);
  const handleCloseCloneModal = () => setOpenCloneModal(false);

  const handleSelectPageToClone = (pageId) => {
    // URL me cloneFrom parameter bhej rahe hain
    navigate(`/pages/create?cloneFrom=${pageId}`);
    handleCloseCloneModal();
  };

  const handleEdit = (pageId) => {
    navigate(`/pages/edit/${pageId}`);
  };

  const handleCreate = () => {
    navigate("/pages/create");
  };

  return (
    <Box m="1.5rem 2.5rem">
      {/* Top Bar */}

      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between" // Yeh theek hai, Header aur buttons group ke beech space dega
        mb={3}
      >
        <Header
          title="PAGES"
          subtitle="Create and manage your MERN Stack landing pages"
        />

        {/* Dono buttons ko ek Box me wrap karein */}
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              background: "#3b82f6",
              "&:hover": { background: "#2563eb" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              py: 1,
            }}
          >
            Create New Page
          </Button>

          <Button
            variant="outlined"
            startIcon={<FileCopyOutlinedIcon />}
            onClick={handleOpenCloneModal}
            sx={{
              borderColor: "rgba(255,255,255,0.2)",
              color: "text.primary",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              py: 1,
              "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
            }}
          >
            Clone Existing
          </Button>
        </Box>
      </Box>
      {/* Loading Skeletons */}
      {loading && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
          gap={2}
        >
          {[1, 2, 3].map((n) => (
            <Skeleton key={n} variant="rounded" height={180} />
          ))}
        </Box>
      )}

      {/* Empty State */}
      {!loading && pages.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={10}
          sx={{
            background: "rgba(255,255,255,0.03)",
            border: "1px dashed rgba(255,255,255,0.12)",
            borderRadius: "12px",
          }}
        >
          <WebAssetIcon sx={{ fontSize: 52, color: "grey.600", mb: 2 }} />
          <Typography variant="h6" fontWeight={500} mb={0.5}>
            No pages yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Click "Create New Page" to build your first landing page.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ textTransform: "none", borderRadius: "8px" }}
          >
            Create New Page
          </Button>
        </Box>
      )}

      {/* Pages Grid */}
      {!loading && pages.length > 0 && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
          gap={2}
        >
          {pages.map((page) => (
            <Card
              key={page._id}
              sx={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
                transition: "border-color 0.2s",
                "&:hover": { borderColor: "rgba(255,255,255,0.3)" },
              }}
            >
              {/* Thumbnail placeholder */}
              <Box
                sx={{
                  height: 90,
                  background: "rgba(255,255,255,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "12px 12px 0 0",
                }}
              >
                <WebAssetIcon sx={{ fontSize: 32, color: "grey.600" }} />
              </Box>

              <CardContent sx={{ pb: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  noWrap
                  title={page.title}
                >
                  {page.title || "Untitled Page"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                >
                  /{page.slug || "no-slug"}
                </Typography>
                <Box mt={1}>
                  {/* <Chip
                    label={page.status || "Draft"}
                    size="small"
                    sx={{
                      fontSize: "11px",
                      height: 22,
                      background:
                        page.status === "published"
                          ? "rgba(46,125,50,0.2)"
                          : "rgba(230,81,0,0.15)",
                      color:
                        page.status === "published" ? "#81c784" : "#ffb74d",
                    }}
                  /> */}

                  <Chip
                    label={page.isPublished ? "Published" : "Draft"}
                    sx={{
                      background: page.isPublished
                        ? "rgba(46,125,50,0.2)"
                        : "rgba(230,81,0,0.15)",
                      color: page.isPublished ? "#81c784" : "#ffb74d",
                    }}
                  />
                </Box>
              </CardContent>

              {/* <CardActions sx={{ justifyContent: "flex-end", pt: 0.5 }}>
                <Tooltip title="Edit Page">
                  <IconButton size="small" onClick={() => handleEdit(page._id)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {page.slug && (
                  <Tooltip title="View Site">
                    <IconButton
                      size="small"
                      onClick={() => window.open(`/${page.slug}`, "_blank")}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </CardActions> */}
              <CardActions sx={{ justifyContent: "flex-end", pt: 0.5 }}>
                <Tooltip title="Delete Page">
                  <IconButton
                    size="small"
                    onClick={(e) => handleDelete(page._id, e)}
                    disabled={deletingId === page._id}
                    sx={{
                      color: "error.main",
                      opacity: 0.6,
                      "&:hover": {
                        opacity: 1,
                        background: "rgba(211,47,47,0.1)",
                      },
                    }}
                  >
                    {deletingId === page._id ? (
                      <CircularProgress size={14} color="error" />
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Page">
                  <IconButton size="small" onClick={() => handleEdit(page._id)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* {page.slug && (
                  <Tooltip title="View Site">
                    <IconButton
                      size="small"
                      onClick={() => window.open(`/${page.slug}`, "_blank")}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )} */}
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Clone Pages Modal */}
      <Dialog
        open={openCloneModal}
        onClose={handleCloseCloneModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Select a Page to Clone
          <IconButton onClick={handleCloseCloneModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
              gap={2}
            >
              <Skeleton variant="rounded" height={100} />
              <Skeleton variant="rounded" height={100} />
            </Box>
          ) : pages.length === 0 ? (
            <Typography textAlign="center" color="text.secondary" py={4}>
              No existing pages to clone. Create one first!
            </Typography>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))"
              gap={2}
              sx={{ p: 1 }}
            >
              {pages.map((page) => (
                <Card
                  key={page._id}
                  sx={{
                    cursor: "pointer",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    transition: "border-color 0.2s",
                    "&:hover": { borderColor: "#3b82f6" },
                  }}
                  onClick={() => handleSelectPageToClone(page._id)}
                >
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                      {page.title || "Untitled Page"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      /{page.slug || "no-slug"}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ClonePage;
